import { useRef, useState, useEffect } from 'react';
import { Star, Play, Pause, Volume2, MessageSquare, Loader2, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Textarea } from '../ui/textarea';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

function formatTime(sec) {
  if (!sec || isNaN(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function RatingHistogram({ artistId, fallbackReviews }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.getArtistRatingStats(artistId)
      .then(res => { if (!cancelled) setStats(res.data); })
      .catch(() => {
        // Fallback: compute from currently loaded reviews so component still shows something
        if (cancelled) return;
        const dist = { '5': 0, '4': 0, '3': 0, '2': 0, '1': 0 };
        (fallbackReviews || []).forEach(r => {
          const k = String(r.rating);
          if (dist[k] !== undefined) dist[k] += 1;
        });
        const total = fallbackReviews?.length || 0;
        const average = total ? fallbackReviews.reduce((s, r) => s + r.rating, 0) / total : 0;
        setStats({ total, average: Math.round(average * 10) / 10, distribution: dist });
      });
    return () => { cancelled = true; };
  }, [artistId, fallbackReviews]);

  if (!stats || stats.total === 0) return null;

  const rows = [5, 4, 3, 2, 1].map(star => {
    const count = stats.distribution[String(star)] || 0;
    const pct = stats.total > 0 ? (count / stats.total) * 100 : 0;
    return { star, count, pct };
  });

  return (
    <div className="bg-surface border border-[#262626] rounded-xl p-5 mb-6" data-testid="rating-histogram">
      <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-6 items-center">
        <div className="text-center sm:text-left">
          <div className="text-4xl font-heading font-bold text-lime" data-testid="avg-rating-large">{stats.average.toFixed(1)}</div>
          <div className="flex items-center gap-0.5 justify-center sm:justify-start mt-1">
            {[1, 2, 3, 4, 5].map(s => (
              <Star key={s} className={`w-4 h-4 ${s <= Math.round(stats.average) ? 'text-lime fill-lime' : 'text-[#333]'}`} />
            ))}
          </div>
          <div className="text-xs text-[#A1A1AA] mt-1">{stats.total} {stats.total === 1 ? 'review' : 'reviews'}</div>
        </div>
        <div className="space-y-1.5">
          {rows.map(row => (
            <div key={row.star} className="flex items-center gap-3 text-xs" data-testid={`histogram-row-${row.star}`}>
              <span className="w-8 flex items-center gap-0.5 text-[#A1A1AA]">
                {row.star}<Star className="w-3 h-3 fill-current" />
              </span>
              <div className="flex-1"><Progress value={row.pct} className="h-1.5" /></div>
              <span className="w-8 text-right text-[#A1A1AA] font-mono">{row.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ArtistReplyBlock({ review, canReply, onReplied }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(review.artist_reply || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.replyToReview(review.id, text.trim());
      toast.success(text.trim() ? 'Reply posted' : 'Reply removed');
      setEditing(false);
      onReplied?.({ ...review, artist_reply: res.data.reply, artist_reply_at: res.data.artist_reply_at });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save reply');
    } finally {
      setSaving(false);
    }
  };

  if (!editing && !review.artist_reply && !canReply) return null;

  if (editing) {
    return (
      <div className="mt-3 pl-4 border-l-2 border-lime/40 space-y-2" data-testid={`artist-reply-editor-${review.id}`}>
        <label className="text-xs text-[#A1A1AA] flex items-center gap-1"><MessageSquare className="w-3 h-3" />Your reply (public)</label>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Thank the buyer, address concerns, or add context…"
          maxLength={500}
          className="input-dark min-h-[70px]"
          data-testid={`artist-reply-input-${review.id}`}
        />
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving} className="btn-primary text-xs h-8 px-3" data-testid={`artist-reply-save-${review.id}`}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
            {review.artist_reply ? 'Update' : 'Post'} reply
          </Button>
          <Button variant="ghost" onClick={() => { setEditing(false); setText(review.artist_reply || ''); }} className="text-xs h-8 px-3">Cancel</Button>
        </div>
      </div>
    );
  }

  if (review.artist_reply) {
    return (
      <div className="mt-3 pl-4 border-l-2 border-lime/40" data-testid={`artist-reply-${review.id}`}>
        <div className="flex items-center gap-2 mb-1">
          <MessageSquare className="w-3 h-3 text-lime" />
          <span className="text-xs text-lime font-medium">Artist reply</span>
          {review.artist_reply_at && (
            <span className="text-xs text-[#555]">{new Date(review.artist_reply_at).toLocaleDateString()}</span>
          )}
        </div>
        <p className="text-sm text-[#A1A1AA] whitespace-pre-wrap">{review.artist_reply}</p>
        {canReply && (
          <button onClick={() => setEditing(true)} className="text-xs text-[#666] hover:text-lime mt-1" data-testid={`artist-reply-edit-btn-${review.id}`}>Edit reply</button>
        )}
      </div>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="mt-3 inline-flex items-center gap-1.5 text-xs text-lime hover:text-lime/80 font-medium"
      data-testid={`artist-reply-open-${review.id}`}
    >
      <MessageSquare className="w-3.5 h-3.5" />
      Reply publicly
    </button>
  );
}

export function ReviewsSection({ reviews: initialReviews, canSeePrices, artistId, artistUserId }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState(initialReviews || []);
  useEffect(() => { setReviews(initialReviews || []); }, [initialReviews]);

  if (!canSeePrices || reviews.length === 0) return null;

  const canReplyAsArtist = !!(user && user.role === 'artist' && artistUserId && user.id === artistUserId);

  return (
    <div className="mt-12" data-testid="reviews-section">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl font-bold">Reviews</h2>
      </div>

      {artistId && <RatingHistogram artistId={artistId} fallbackReviews={reviews} />}

      <div className="space-y-4">
        {reviews.map(review => (
          <div key={review.id} className="bg-surface border border-[#262626] rounded-xl p-5" data-testid={`review-${review.id}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-lime/20 flex items-center justify-center">
                  <span className="text-xs font-bold text-lime">{(review.user_name || 'A')[0].toUpperCase()}</span>
                </div>
                <span className="font-medium text-sm">{review.user_name || 'Anonymous'}</span>
              </div>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map(s => (
                  <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'text-lime fill-lime' : 'text-[#333]'}`} />
                ))}
              </div>
            </div>
            {review.comment && <p className="text-sm text-[#A1A1AA]">{review.comment}</p>}
            <p className="text-xs text-[#555] mt-2">{new Date(review.created_at).toLocaleDateString()}</p>
            <ArtistReplyBlock
              review={review}
              canReply={canReplyAsArtist}
              onReplied={(updated) => setReviews(prev => prev.map(r => r.id === updated.id ? updated : r))}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export function AudioSamplePlayer({ sampleUrl }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) { audioRef.current.pause(); } else { audioRef.current.play(); }
    setIsPlaying(!isPlaying);
  };

  if (!sampleUrl) return null;

  return (
    <div className="bg-surface border border-[#262626] rounded-xl p-4" data-testid="audio-sample-player">
      <div className="flex items-center gap-3 mb-3">
        <Volume2 className="w-5 h-5 text-lime" />
        <span className="font-medium text-sm">Audio Sample</span>
        <Badge className="bg-lime/10 text-lime border-0 text-xs">Preview</Badge>
      </div>
      <div className="flex items-center gap-3">
        <Button onClick={togglePlay} variant="outline" size="icon" className="w-10 h-10 rounded-full border-lime text-lime hover:bg-lime/10" data-testid="sample-play-btn">
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
        </Button>
        <div className="flex-1">
          <Progress value={audioDuration ? (audioProgress / audioDuration) * 100 : 0} className="h-1.5" />
          <div className="flex justify-between mt-1">
            <span className="text-xs text-[#666]">{formatTime(audioProgress)}</span>
            <span className="text-xs text-[#666]">{formatTime(audioDuration)}</span>
          </div>
        </div>
      </div>
      <audio
        ref={audioRef}
        src={sampleUrl}
        onTimeUpdate={() => audioRef.current && setAudioProgress(audioRef.current.currentTime)}
        onLoadedMetadata={() => audioRef.current && setAudioDuration(audioRef.current.duration)}
        onEnded={() => setIsPlaying(false)}
      />
    </div>
  );
}
